import classNames from 'classnames';
import { Tile, TileProps } from '@components/tile';
import { LazyLoadWrapper } from '@components/utils';

import './CircularTile.scss';

export interface CircularTileProps
  extends Omit<TileProps, 'bgProps' | 'aspectRatio' | 'hasBackgroundGrowEffect'> {
  /** Artwork source rendered centered inside the circle */
  src: string;
  /** Alternative text for the artwork */
  alt: string;
  /** Should the artwork use lazy loading */
  lazy?: boolean;
  /** Hides the gradient backdrop and sizes the artwork to fill the tile */
  transparent?: boolean;
}

/**
 * Circular content tile for circular carousels — artwork centered in a 1:1 circle over a gradient backdrop.
 * @param src - Artwork source rendered centered inside the circle
 * @param alt - Alternative text for the artwork
 * @param lazy - Should the artwork use lazy loading
 * @param transparent - Hides the gradient backdrop and sizes the artwork to fill the tile
 * @param children - Any React.ReactNode element rendered over the circle
 * @param className - Additional classnames to be included as customization
 */
export const CircularTile: React.FC<CircularTileProps> = ({
  src,
  alt,
  lazy,
  transparent,
  className,
  children,
  ...rest
}) => (
  <Tile
    aspectRatio="1 / 1"
    className={classNames(
      'vds-circular-tile',
      { 'vds-circular-tile--transparent': transparent },
      className,
    )}
    {...rest}>
    {!transparent && <span className="vds-circular-tile__backdrop" />}
    <span className="vds-circular-tile__artwork">
      <LazyLoadWrapper className="vds-circular-tile__artwork-img" src={src} alt={alt} lazy={lazy} />
    </span>
    {children && <span className="vds-circular-tile__content">{children}</span>}
  </Tile>
);
